// hooks/SyncCoordinator.js - Complete implementation
import React, { createContext, useContext, useCallback, useRef } from 'react';
import logger from '../services/logger';

class SyncCoordinator {
  constructor() {
    this.locks = new Map();
    this.queue = [];
    this.processing = false;
    this.stateVersion = 0;
    this.listeners = new Set();
  }
  
  // Increment version on state changes
  incrementVersion() {
    this.stateVersion++;
    this.notifyListeners();
  }
  
  notifyListeners() {
    this.listeners.forEach(listener => listener(this.stateVersion));
  }
  
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  
  // Acquire a lock for an operation
  async acquireLock(key, timeout = 5000) {
    // Increase timeout for auth operations that can take longer
    if (key === 'auth') {
      timeout = 15000; // 15 seconds for auth operations
    }
    const lockId = Math.random().toString(36).substring(7);
    
    return new Promise((resolve, reject) => {
      const tryAcquire = () => {
        if (!this.locks.has(key)) {
          this.locks.set(key, { id: lockId, timestamp: Date.now() });
          
          // Auto-release after timeout
          setTimeout(() => {
            const lock = this.locks.get(key);
            if (lock && lock.id === lockId) {
              logger.warn(`Auto-releasing stale lock: ${key}`);
              this.locks.delete(key);
            }
          }, timeout);
          
          resolve({
            release: () => {
              const lock = this.locks.get(key);
              if (lock && lock.id === lockId) {
                this.locks.delete(key);
              }
            }
          });
          return true;
        }
        return false;
      };
      
      if (tryAcquire()) return;
      
      // Wait and retry
      const startTime = Date.now();
      const interval = setInterval(() => {
        if (tryAcquire()) {
          clearInterval(interval);
        } else if (Date.now() - startTime > timeout) {
          clearInterval(interval);
          reject(new Error(`Lock timeout for ${key}`));
        }
      }, 10);
    });
  }
  
  // Queue an operation to prevent races
  async queueOperation(operation, priority = 0) {
    return new Promise((resolve, reject) => {
      this.queue.push({ operation, priority, resolve, reject });
      this.queue.sort((a, b) => b.priority - a.priority);
      this.processQueue();
    });
  }
  
  async processQueue() {
    if (this.processing || this.queue.length === 0) return;
    
    this.processing = true;
    
    while (this.queue.length > 0) {
      const { operation, resolve, reject } = this.queue.shift();
      
      try {
        const result = await operation();
        resolve(result);
      } catch (error) {
        reject(error);
      }
      
      // Small delay between operations
      await new Promise(r => setTimeout(r, 10));
    }
    
    this.processing = false;
  }
  
  // Debug info
  getDebugInfo() {
    return {
      locks: Array.from(this.locks.entries()),
      queueLength: this.queue.length,
      processing: this.processing,
      stateVersion: this.stateVersion
    };
  }
}

// Global instance
const syncCoordinator = new SyncCoordinator();

// Make it available for debugging
if (typeof window !== 'undefined') {
  window._syncCoordinator = syncCoordinator;
}

// React Context
const SyncContext = createContext();

// Provider component
export function SyncProvider({ children }) {
  const versionRef = useRef(0);
  
  const runSync = useCallback(async (key, operation) => {
    const lock = await syncCoordinator.acquireLock(key);
    try {
      const result = await operation();
      syncCoordinator.incrementVersion();
      return result;
    } finally {
      lock.release();
    }
  }, []);
  
  const queueSync = useCallback((operation, priority = 0) => {
    return syncCoordinator.queueOperation(async () => {
      const result = await operation();
      syncCoordinator.incrementVersion();
      return result;
    }, priority);
  }, []);
  
  const value = { runSync, queueSync, syncCoordinator };
  
  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  );
}

// Hook
export function useSyncOperation() {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSyncOperation must be used within SyncProvider');
  }
  return context;
}

// Specific sync operations for common tasks
export const SyncOperations = {
  TRACK_CHANGE: 'track-change',
  PLAYLIST_UPDATE: 'playlist-update',
  PLAYBACK_CONTROL: 'playback-control',
  DJ_INTRO: 'dj-intro',
  SEARCH: 'search',
  AUTH: 'auth'
};